import { ChevronDown, ChevronUp, Clock, Star, Target, Users } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PriceDisplay } from "@/components/ui/price-display";
import type { CustomerAnalyticsQuery } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";

interface CustomerServiceIntelligenceCardProps {
  analytics?: NonNullable<CustomerAnalyticsQuery["customerAnalytics"]>;
  loading?: boolean;
}

export function CustomerServiceIntelligenceCard({
  analytics,
  loading = false,
}: CustomerServiceIntelligenceCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (loading || !analytics) {
    return <ServiceIntelligenceSkeleton />;
  }

  const { servicePreferences, staffPreferences } = analytics;
  const topService = servicePreferences[0];
  const preferredStaff = staffPreferences.find((s) => s.isPreferredStaff) || staffPreferences[0];

  const getLoyaltyColor = (level: string) => {
    switch (level) {
      case "highly_loyal":
        return "bg-green-100 text-green-900 border-green-300 dark:bg-green-950 dark:text-green-100";
      case "loyal":
        return "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-100";
      case "returning":
        return "bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950 dark:text-yellow-100";
      case "trial":
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100";
      default:
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100";
    }
  };

  const getRelationshipColor = (strength: string) => {
    switch (strength) {
      case "primary":
        return "bg-purple-100 text-purple-900 border-purple-300 dark:bg-purple-950 dark:text-purple-100";
      case "regular":
        return "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-100";
      case "occasional":
        return "bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950 dark:text-yellow-100";
      case "trial":
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100";
      default:
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100";
    }
  };

  const formatDaysAgo = (days: number) => {
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30)
      return `${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? "s" : ""} ago`;
    return `${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? "s" : ""} ago`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            Service Intelligence
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-muted-foreground hover:text-foreground"
          >
            {isExpanded ? (
              <>
                <span className="mr-1">Collapse</span>
                <ChevronUp className="w-4 h-4" />
              </>
            ) : (
              <>
                <span className="mr-1">Expand</span>
                <ChevronDown className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Collapsed State - Top preferences */}
        {!isExpanded ? (
          <>
            {/* Top Service */}
            {topService && (
              <div className="bg-muted p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Top Service</span>
                  <Badge variant="outline" className={getLoyaltyColor(topService.loyaltyLevel)}>
                    {topService.loyaltyLevel.replace("_", " ")}
                  </Badge>
                </div>
                <div className="text-lg font-semibold">{topService.serviceName}</div>
                <div className="text-sm text-muted-foreground">
                  {topService.percentageOfVisits}% of visits • Last:{" "}
                  {formatDaysAgo(topService.daysSinceLastBooked)}
                </div>
              </div>
            )}

            {/* Preferred Staff */}
            {preferredStaff && (
              <div className="bg-muted p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Preferred Staff</span>
                    {preferredStaff.isPreferredStaff && (
                      <Star className="w-3 h-3 text-yellow-500 fill-current" />
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className={getRelationshipColor(preferredStaff.relationshipStrength)}
                  >
                    {preferredStaff.relationshipStrength}
                  </Badge>
                </div>
                <div className="text-lg font-semibold">{preferredStaff.workerName}</div>
                <div className="text-sm text-muted-foreground">
                  {preferredStaff.percentageOfVisits}% of visits • Last:{" "}
                  {formatDaysAgo(preferredStaff.daysSinceLastVisit)}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Expanded State - Full details */}

            {/* Service Preferences */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Target className="w-4 h-4" />
                Service Preferences
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {servicePreferences.slice(0, 4).map((service, index) => (
                  <div key={service.serviceName} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            index === 0
                              ? "bg-green-500"
                              : index === 1
                                ? "bg-blue-500"
                                : index === 2
                                  ? "bg-orange-500"
                                  : "bg-gray-400"
                          }`}
                        />
                        <span className="font-medium text-sm">{service.serviceName}</span>
                      </div>
                      <Badge variant="outline" className={getLoyaltyColor(service.loyaltyLevel)}>
                        {service.loyaltyLevel.replace("_", " ")}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Bookings:</span>
                        <span>
                          {service.bookingCount} ({service.percentageOfVisits}%)
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Spent:</span>
                        <span>{<PriceDisplay amount={service.totalSpent} />}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Last Booked:</span>
                        <span>{formatDaysAgo(service.daysSinceLastBooked)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Staff Relationships */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Staff Relationships
              </h4>
              <div className="space-y-3">
                {staffPreferences.slice(0, 3).map((staff) => (
                  <div key={staff.workerId} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                          {staff.firstName[0]}
                          {staff.lastName[0]}
                        </div>
                        <div>
                          <span className="font-medium">{staff.workerName}</span>
                          {staff.isPreferredStaff && (
                            <Star className="w-3 h-3 text-yellow-500 fill-current ml-1 inline" />
                          )}
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={getRelationshipColor(staff.relationshipStrength)}
                      >
                        {staff.relationshipStrength}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground">Visits</div>
                        <div className="font-medium">
                          {staff.bookingCount} ({staff.percentageOfVisits}%)
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Total Spent</div>
                        <div className="font-medium">
                          {<PriceDisplay amount={staff.totalSpent} />}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Last Visit</div>
                        <div className="font-medium">{formatDaysAgo(staff.daysSinceLastVisit)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Booking Patterns */}
            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg">
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Booking Patterns
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground">Visit Consistency</div>
                  <div className="font-medium capitalize">
                    {analytics.visitFrequency.visitConsistency.replace("_", " ")}
                  </div>
                </div>
                <div>
                  <div className="text-muted-foreground">Next Predicted Visit</div>
                  <div className="font-medium">
                    {timezoneUtils
                      .fromISO(analytics.visitFrequency.predictedNextVisit)
                      .toLocaleString(DateTime.DATE_MED)}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ServiceIntelligenceSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-40" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-20" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3" />
        </div>
        <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3" />
        </div>
      </CardContent>
    </Card>
  );
}
